import React, { useEffect, useState, useCallback } from 'react';
import { Table, Input, Pagination, message, Switch, Select } from 'antd';
import { getRequest, putRequest } from '../../../helpers/apiCall';
import debounce from 'lodash.debounce';

const { Search } = Input;
const { Option } = Select;

const ClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState(''); // '' = All, true = Active, false = Inactive

  const fetchClients = async (page = 1, query = '', active = '') => {
    setLoading(true);
    try {
      let url = `clientlist?page=${page}`;
      if (query) url += `&search=${query}`;
      if (active !== '') url += `&active=${active}`; // send active filter in payload

      const resp = await getRequest(url);
      if (!resp?.data?.success) {
        message.error("Error fetching clients list");
        return;
      }

      const data = resp?.data;
      setClients(data?.data?.data || []);
      setPagination({
        current: data?.data?.current_page,
        pageSize: data?.data?.per_page,
        total: data?.data?.total,
      });
    } catch (error) {
      console.error(error);
      message.error("Error fetching clients list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients(1, searchTerm, activeFilter);
  }, [searchTerm, activeFilter]);

  const handlePageChange = (page) => {
    fetchClients(page, searchTerm, activeFilter);
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 500),
    []
  );

  const handleSearch = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleToggleActive = async (checked, record) => {
    try {
      setLoading(true);
      const payload = {
        active: checked,
        user_id: record.id,
      };
      const resp = await putRequest('client-inactivate', payload);

      if (resp?.data?.success) {
        message.success(`Client ${checked ? 'activated' : 'deactivated'} successfully`);
        setClients((prev) =>
          prev.map((c) =>
            c.id === record.id ? { ...c, active: checked } : c
          )
        );
      } else {
        message.error(resp?.data?.message || "Action failed");
      }
    } catch (error) {
      console.error(error);
      message.error("Error updating client status");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'First Name', dataIndex: 'first_name', key: 'first_name' },
    { title: 'Last Name', dataIndex: 'last_name', key: 'last_name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Email Verified', dataIndex: 'email_verified_at', key: 'email_verified_at', render: (text) => (text ? 'Yes' : 'No') },
    { title: 'First Login', dataIndex: 'first_login', key: 'first_login' },
    { title: 'Last Login', dataIndex: 'last_login', key: 'last_login' },
    { title: 'Login Mode', dataIndex: 'mode_of_login', key: 'mode_of_login' },
    { title: 'Stripe ID', dataIndex: 'stripe_id', key: 'stripe_id' },
    {
      title: 'Activity Status',
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={(checked) => handleToggleActive(checked, record)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
  ];

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Clients Management</h2>
      <div style={{ display: 'flex', gap: '10px', marginBottom: 20 }}>
        <Search
          placeholder="Search by name or email"
          allowClear
          onChange={handleSearch}
          style={{ width: 300 }}
        />
        <Select
          placeholder="Filter by status"
          style={{ width: 200 }}
          allowClear
          value={activeFilter}
          onChange={(value) => setActiveFilter(value)}
        >
          <Option value="">All Clients</Option>
          <Option value="1">Active Clients</Option>
          <Option value="0">Inactive Clients</Option>
        </Select>
      </div>
      <Table
        columns={columns}
        dataSource={clients}
        rowKey="id"
        loading={loading}
        pagination={false}
      />
      <div style={{ textAlign: 'right', marginTop: 20 }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default ClientsManagement;
